import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { resolveInitialDisclosuresSendAction } from 'app/pages/loanApplication/LoanHeaders/RedesignedLoanInfoHeader/LoanActionsControls/LoanActionsControls.utils';
import _ from 'lodash';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { getFormBundlesAction } from 'store/forms/actions';
import { selectFormBundles } from 'store/forms/selectors';
// Import the specific module (not the `store/loanApplications/selectors` barrel) — that barrel
// pulls in the ENTIRE selector graph, and this hook is itself imported from
// `LoanActionsControls/hooks` (a barrel several unrelated pages import), which created a
// circular-require cycle that left an unrelated selector `undefined` at module-init time.
import { selectLoanActions } from 'store/loanApplications/selectors/loanActions.selectors';

// Shared by the entry CTA and the wizard Send step so both resolve the SAME confidently-matched
// Initial Disclosures SendFormBundles action — a naive first-match can pick an unrelated test
// action configured on the same company. The action payload carries only the target bundle id, so
// a catalog is needed to read that bundle's NAME.
//
// It must be the COMPANY's configured bundles (`GET /FormBundle`), never the loan-scoped
// `/FormBundle/ForLoan/{loanId}`: that endpoint derives its list from the loan's form DELIVERY
// REQUESTS, so it is empty until a bundle has already been sent. Resolving against it made the
// first send unresolvable — the wizard hid itself on exactly the never-disclosed loans it exists
// to serve (DEV-5694). The company list is `[Authorize]`-only, so brokers can read it.
//
// Stays `isLoading` until the catalog resolves, so callers never read "not loaded yet" as "no match".
const useResolvedDisclosuresSendAction = loanId => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(!!loanId);

  const loanActions = useSelector(selectLoanActions);
  const formBundlesById = useSelector(selectFormBundles);

  useEffectSafeStrictMode(() => {
    if (!loanId) {
      setIsLoading(false);

      return undefined;
    }

    const mountedRef = { current: true };
    setIsLoading(true);

    dispatch(getFormBundlesAction({ silent: true }))
      .catch(_.noop)
      .finally(() => {
        if (mountedRef.current) {
          setIsLoading(false);
        }
      });

    return () => {
      mountedRef.current = false;
    };
  }, [dispatch, loanId]);

  const sendAction = isLoading
    ? null
    : resolveInitialDisclosuresSendAction(loanActions, formBundlesById);

  return { sendAction, isLoading };
};

export default useResolvedDisclosuresSendAction;
